/**
 * Tiny identity-scoped in-memory cache for read-only API responses.
 *
 * Every entry is namespaced by the current authentication identity and role.
 * Public data may be duplicated across scopes, but private customer, seller,
 * and admin responses can never be read by another account after a session
 * transition. The unresolved bootstrap scope is intentionally separate from
 * guest so pages cannot hydrate anonymous data before auth finishes.
 */

const _cache = new Map();
let _activeScope = "auth:unresolved";

function normalizeScope(scope) {
  const value = String(scope || "").trim();
  return value || "auth:unresolved";
}

function scopedKey(key, scope = _activeScope) {
  return `${normalizeScope(scope)}::${String(key)}`;
}

export function setCacheScope(scope) {
  _activeScope = normalizeScope(scope);
  return _activeScope;
}

export function getCacheScope() {
  return _activeScope;
}

export function getCached(key) {
  const resolved = scopedKey(key);
  return _cache.has(resolved) ? _cache.get(resolved) : undefined;
}

export function setCached(key, value) {
  _cache.set(scopedKey(key), value);
}

export function clearCached(key) {
  if (key === undefined) {
    _cache.clear();
  } else {
    _cache.delete(scopedKey(key));
  }
}

export function clearCachedScope(scope = _activeScope) {
  const prefix = `${normalizeScope(scope)}::`;
  for (const key of _cache.keys()) {
    if (key.startsWith(prefix)) _cache.delete(key);
  }
}
